'use client'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { EASE, Eyebrow, HAIRLINE, MODULES, useReveal } from './shared'

/* The bookend. The hero opened on six hairline cells with hollow nodes over a
   circuit field fading downward; the page closes on the same six cells, every
   node now filled, over the same field fading upward. The rail's story
   finishes rather than the page merely stopping. */

/* Kept here rather than on MODULES itself: the hero rail and the datastore bus
   render the same list as diagram parts, and only this closing pass is meant to
   be navigable. Keys are MODULES keys; the two without a page stay plain text. */
const MODULE_HREF: Record<string, string | undefined> = {
  hrms: '/products/hrms',
  payroll: '/products/payroll',
  accounts: '/products/accounts',
  lms: '/products/lms',
  cloud: '/solutions/cloud-infrastructure',
  ai: '/solutions/ai-infrastructure',
}

export function UdcCta() {
  const rail = useReveal<HTMLDivElement>(0.25)

  return (
    // No top margin: the image band above fades to transparent directly onto
    // this section's own circuit field.
    <section className="relative overflow-hidden">
      {/* The field is anchored high and cut off above the rail, so no trace
          ever runs through the closing cells or their caption. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[70%]"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div className="container relative z-10 mx-auto py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl">
          <Eyebrow>Ready when you are</Eyebrow>

          <h2 className="font-heading mt-6 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[56px] lg:leading-[1.1]">
            <ChromaText>Bring the whole campus under one roof.</ChromaText>
          </h2>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            We migrate what you already run (registers, ledgers, spreadsheets,
            whatever the last vendor left behind) and hand back one system with
            all of it inside.
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="/migration-service">Talk about migration</a>
            </Button>
          </div>
        </div>
      </div>

      {/* The rail, closed out — and, on the closing pass only, each module is
          the way into its own page. The hero rail and the datastore bus stay
          plain: those two are diagrams of one product, not a menu. */}
      <div ref={rail.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="grid grid-cols-2 gap-px bg-border/70 sm:grid-cols-3 lg:grid-cols-6">
          {MODULES.map((m, i) => {
            const href = MODULE_HREF[m.key]
            const body = (
              <>
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full bg-emerald-600 dark:bg-primary-active"
                />
                <span className="font-heading text-sm font-normal leading-none text-foreground sm:text-base">
                  {m.name}
                </span>
              </>
            )
            const cellClass =
              'flex items-center gap-2.5 bg-background px-4 py-5 sm:px-5'
            const style = {
              opacity: rail.shown ? 1 : 0,
              transition: `opacity 460ms ${EASE} ${i * 70}ms`,
            }

            return href ? (
              <Link
                key={m.key}
                href={href}
                aria-label={`${m.name}: ${m.line}`}
                className={cn(
                  cellClass,
                  'transition-colors hover:bg-accent/60',
                  'focus:outline-none focus-visible:outline-2 focus-visible:-outline-offset-2',
                  'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
                )}
                style={style}
              >
                {body}
              </Link>
            ) : (
              <div key={m.key} className={cellClass} style={style}>
                {body}
              </div>
            )
          })}
        </div>
      </div>

      <div className="container mx-auto">
        <p className="py-5 text-center text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          All six, live from day one
        </p>
      </div>
    </section>
  )
}
